import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import React from "react";

interface UsageLimitsProps {
  currentUsers: number;
  currentEditors: number;
  currentPropositions: number;
  currentSegments: number;
  maxUsers?: number;
  maxEditors?: number;
  maxPropositions?: number;
  maxSegments?: number;
}

export const UsageLimits: React.FC<UsageLimitsProps> = ({
  currentUsers,
  currentEditors,
  currentPropositions,
  currentSegments,
  maxUsers,
  maxEditors,
  maxPropositions,
  maxSegments,
}) => {
  const calculatePercentage = (current: number, max?: number): number => {
    if (!max) return 0;
    return Math.min(100, Math.round((current / max) * 100));
  };

  const getProgressColor = (percentage: number): string => {
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  const UsageItem = ({
    label,
    current,
    max,
  }: {
    label: string;
    current: number;
    max?: number;
  }) => {
    const percentage = calculatePercentage(current, max);
    const isAtLimit = max && current >= max;
    const isNearLimit = max && percentage >= 90;

    return (
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2 flex-shrink-0">
            <span className="text-sm font-medium text-gray-700">{label}</span>
            {isAtLimit && (
              <ExclamationCircleIcon className="h-4 w-4 text-red-500" />
            )}
          </div>
          <span className="text-sm text-gray-600 text-right">
            {current}
            {max ? ` / ${max}` : " (Unlimited)"}
          </span>
        </div>
        {max && (
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`${getProgressColor(percentage)} h-2 rounded-full transition-all duration-300`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        )}
        {isNearLimit && !isAtLimit && (
          <p className="text-xs text-yellow-600">
            You&apos;re approaching your {label.toLowerCase()} limit
          </p>
        )}
        {isAtLimit && (
          <p className="text-xs text-red-600">
            You&apos;ve reached your {label.toLowerCase()} limit. Upgrade to add
            more.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Usage & Limits</h3>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <UsageItem
            label="Total Users"
            current={currentUsers}
            max={maxUsers}
          />
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">
                Editor Users
              </span>
              <span className="text-sm text-gray-600 text-right">
                {currentEditors} {currentEditors === 1 ? "editor" : "editors"}
              </span>
            </div>
            <p className="text-xs text-gray-500">
              {currentEditors > 1
                ? `First editor included, ${currentEditors - 1} additional`
                : "Included in base price"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <UsageItem
            label="Propositions"
            current={currentPropositions}
            max={maxPropositions}
          />
          <UsageItem
            label="Segments"
            current={currentSegments}
            max={maxSegments}
          />
        </div>
      </div>

      {(!maxUsers || !maxEditors || !maxPropositions || !maxSegments) && (
        <div className="mt-4 bg-blue-50 rounded-md p-4">
          <p className="text-sm text-blue-700">
            Some features have unlimited usage on your current plan.
          </p>
        </div>
      )}
    </div>
  );
};
